import React from 'react';

const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      step: '01',
      title: 'Subscribe & Setup',
      description: 'Choose your plan and complete your health profile. Our team will review your medical history and assign you a primary care physician.',
      icon: 'ri-user-add-line',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      step: '02',
      title: 'Schedule Your Visit',
      description: 'Book appointments through our app or website. Need urgent care? Request an on-demand consultation with guaranteed 24-hour response.',
      icon: 'ri-calendar-check-line',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      step: '03',
      title: 'Connect with Your Doctor',
      description: 'Meet with board-certified physicians via secure video calls. Discuss symptoms, get diagnoses, and receive personalized treatment plans.',
      icon: 'ri-video-chat-line',
      color: 'bg-green-100 text-green-600'
    },
    {
      step: '04',
      title: 'Receive Care & Follow-up',
      description: 'Get prescriptions sent to your pharmacy, access lab results, and schedule follow-up visits. Your care team monitors your progress continuously.',
      icon: 'ri-heart-pulse-line',
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Getting started with Virtual Primary Care is simple. Follow these four easy steps to begin your healthcare journey.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gray-200 transform translate-x-4">
                  <div className="absolute right-0 top-1/2 transform translate-y-1/2 w-2 h-2 bg-gray-300 rounded-full"></div>
                </div>
              )}
              
              <div className="text-center">
                <div className={`w-20 h-20 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <i className={`${step.icon} text-3xl`}></i>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm font-bold text-gray-400 tracking-wider">
                    STEP {step.step}
                  </span>
                  <h3 className="text-xl font-semibold text-gray-900 mt-2">
                    {step.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl p-8 lg:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-xl text-teal-100 mb-6">
                Join thousands of patients who have transformed their healthcare experience with Virtual Primary Care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap">
                  Start Free Trial
                </button>
                <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap">
                  Schedule Demo
                </button>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/7a56f579b4289b2c37b51e84e581b78b.jpg"
                alt="Healthcare Team"
                className="w-full h-auto rounded-xl shadow-2xl object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-teal-500 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">50+ Physicians Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;